import { t } from 'i18next';
import { blue } from 'theme/colors';
import { Box, SvgIcon, Tooltip } from '@mui/material';
import { IconCircleCheck, IconInfoCircle, IconXboxX } from '@tabler/icons-react';
import { Button } from '@atoms';
import { useUser } from '@hooks';
import { parseAmount } from '@utils';
import { PendingDispersion } from '../../data/useGetPendingDispersions';
import { SelectedPendingDispersion } from '../../data/useResolvePendingDispersion/hooks/useResolvePendingDispersion';
import styles from '../../styles';

/**
 * Approve / reject buttons for pending dispersion rows
 * @param dispersion - Pending dispersion row
 * @param callback - Function triggered on action click
 */
export default function ActionButtons({
  dispersion,
  callback,
}: {
  dispersion: PendingDispersion;
  callback: (d: SelectedPendingDispersion) => void;
}) {
  const { user } = useUser();
  const isOwnRequest = user?.email?.toLowerCase() === dispersion?.createdBy?.toLowerCase();
  const isPending = dispersion.status === 'PENDING';
  const showOwnRequestInfo = isPending && isOwnRequest;
  const showActions = isPending && !isOwnRequest;

  /**
   * Builds the payload for the confirmation modal
   * @param action - Approve or reject
   */
  const getSelectedDispersion = (action: SelectedPendingDispersion['action']) => ({
    id: dispersion.id,
    companyName: dispersion.companyName || '-',
    formattedAmount: parseAmount(dispersion.attemptedAmount),
    action,
  });

  return (
    <Box display={'flex'} justifyContent={'space-around'}>
      {showOwnRequestInfo && (
        <Tooltip title={t('PENDING_DISPERSIONS:OWN_REQUEST_INFO')} arrow>
          <Box display='flex' alignItems='center'>
            <SvgIcon fontSize='medium'>
              <IconInfoCircle color={blue.main} />
            </SvgIcon>
          </Box>
        </Tooltip>
      )}
      <ActionButton
        {...{
          tooltip: 'COMMON:APPROVE',
          icon: <IconCircleCheck color={blue.main} />,
          callback: () => callback(getSelectedDispersion('APPROVE')),
          show: showActions,
        }}
      />
      <ActionButton
        {...{
          tooltip: 'COMMON:REJECT',
          icon: <IconXboxX color={blue.main} />,
          callback: () => callback(getSelectedDispersion('REJECT')),
          show: showActions,
        }}
      />
    </Box>
  );
}

interface ActionButtonProps {
  tooltip: string;
  icon: React.ReactNode;
  callback: () => void;
  show: boolean;
}

/**
 * Button icon component of action buttons column
 * @param tooltip text to show on tooltip
 * @param icon icon to render inside button
 * @param callback function to trigger on button click
 * @param show if true renders the button, if false renders an empty fragment
 */
const ActionButton = ({ tooltip, icon, callback, show }: ActionButtonProps) => {
  if (!show) return <></>;
  return (
    <Tooltip title={t(tooltip)} arrow>
      <Box onClick={(event) => event.stopPropagation()}>
        <Button
          customStyle={{
            button: styles.actionButton,
            box: styles.actionButtonBox,
          }}
          onClick={(event) => {
            event.stopPropagation();
            callback();
          }}
          content={<SvgIcon fontSize='medium'>{icon}</SvgIcon>}
        />
      </Box>
    </Tooltip>
  );
};
